import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";

function AlertSettingsEditor() {
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then((r) => setDays(r.data.overdue_days ?? 30))
      .catch(() => setDays(30));
  }, []);

  const save = async () => {
    const n = Math.round(Number(days));
    if (!(n >= 1)) { setError("Enter a number of days (1 or more)."); return; }
    setSaving(true);
    setError("");
    try {
      await api.put("/settings", { overdue_days: n });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (days === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="alert-settings-editor">
      <div className="max-w-xs">
        <Label htmlFor="overdue-days">Invoice overdue after (days)</Label>
        <Input
          id="overdue-days"
          type="number"
          min="1"
          step="1"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          data-testid="overdue-days-input"
          className="nums"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">A Sent or Partially Paid invoice past this many days with a balance raises an &ldquo;overdue&rdquo; dashboard alert.</p>
      </div>
      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="overdue-days-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save threshold
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AlertSettingsCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dashboard alerts</CardTitle>
        <CardDescription>Tune when the dashboard flags an invoice as overdue. Level 1 hours and the Level 2 cap come from each matter&apos;s coverage profile.</CardDescription>
      </CardHeader>
      <CardContent><AlertSettingsEditor /></CardContent>
    </Card>
  );
}
